// frontend/src/pages/AgenteDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TornaAlMenu from "../components/TornaAlMenu";

const API_URL = process.env.REACT_APP_API_URL || "";

function KpiCard({ title, value }) {
  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <div className="text-muted">{title}</div>
        <div className="fs-4 fw-bold mt-1">{value}</div>
      </div>
    </div>
  );
}

function euro(n) {
  return `â‚¬ ${Number(n || 0).toFixed(2)}`;
}

function fmtDateTime(s) {
  if (!s) return "-";

  let clean = String(s);

  // Normalizza ISO: alcuni browser non digeriscono i microsecondi (.873084Z)
  clean = clean.replace(/\.\d+Z$/, "Z");

  const d = new Date(clean);
  if (isNaN(d.getTime())) return "-";

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");

  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

/* ---------- helper data/ora ---------- */
const pad2 = (n) => String(n).padStart(2, "0");
const toDateLocal = (val) => {
  if (!val) return null;
  let s = String(val);
  if (s.includes(" ") && !s.includes("T")) s = s.replace(" ", "T");
  const d = new Date(s);
  return isNaN(d) ? null : d;
};
const formatLastOrder = (val) => {
  const d = toDateLocal(val);
  if (!d) return "-";
  const dd = pad2(d.getDate());
  const mm = pad2(d.getMonth() + 1);
  const yyyy = d.getFullYear();
  const hh = pad2(d.getHours());
  const min = pad2(d.getMinutes());
  return `${dd}/${mm}/${yyyy} ${hh}.${min}`;
};
// -------------------------------------

export default function AgenteDashboard() {
  const [storicoSearch, setStoricoSearch] = React.useState("");

  const nav = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [me, setMe] = useState(null);
  const [overview, setOverview] = useState(null);
  const [detail, setDetail] = useState(null);

  const qsUserId = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("user_id");
  }, [location.search]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const token = localStorage.getItem("access");
        const res = await fetch(`${API_URL}/api/me/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.detail || "Errore caricamento profilo");
        setMe(data);
      } catch (e) {
        setErr(e.message || "Errore");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!me) return;

    const role = (me.role || "").toUpperCase();
    const token = localStorage.getItem("access");

    (async () => {
      try {
        setLoading(true);
        setErr("");

        if (qsUserId) {
          const res = await fetch(`${API_URL}/api/agente/dashboard/?user_id=${qsUserId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data?.detail || "Errore caricamento dashboard agente");
          setDetail(data);
          setOverview(null);
          return;
        }

        if (role === "AGENTE") {
          const res = await fetch(`${API_URL}/api/agente/dashboard/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data?.detail || "Errore caricamento dashboard agente");
          setDetail(data);
          setOverview(null);
          return;
        }

        // MADRE/SEDE senza user_id: non ha senso mostrare la dashboard dell'admin.
        // Chiediamo di selezionare un agente dalla lista.
        if (role === "MADRE" || role === "SEDE") {
          setDetail(null);
          setOverview(null);
          setErr("Seleziona un agente dalla lista (pagina Agenti) oppure apri /dashboard-agente?user_id=ID.");
          return;
        }

        const res = await fetch(`${API_URL}/api/agente/dashboard/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.detail || "Errore caricamento overview agenti");

        if (Array.isArray(data.agenti)) {
          setOverview(data);
          setDetail(null);
        } else {
          setDetail(data);
          setOverview(null);
        }
      } catch (e) {
        setErr(e.message || "Errore");
        setOverview(null);
        setDetail(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [me, qsUserId]);

  const statusIt = (code) => {
    const s = (code || "").toUpperCase();
    if (s === "DRAFT") return "Bozza";
    if (s === "SUBMITTED" || s === "INVIATO") return "Inviato";
    if (s === "APPROVED") return "Approvato";
    if (s === "FULFILLED") return "Evaso";
    if (s === "CANCELED") return "Annullato";
    if (s === "PENDING") return "In attesa";
    if (s === "PAID") return "Pagato";
    if (s === "EARNED") return "Maturato";
    return code || "";
  };

  const badgeClassFor = (code) => {
    const s = (code || "").toUpperCase();
    if (s === "PAID") return "badge bg-success";
    if (s === "PENDING") return "badge bg-warning text-dark";
    if (s === "EARNED") return "badge bg-info text-dark";
    if (s === "CANCELED") return "badge bg-danger";
    return "badge bg-secondary";
  };

  return (
    <div className="container mt-4">
      <TornaAlMenu to="/" />
      <h2 className="mb-3">ðŸ“Š Dashboard Agente</h2>

      {err && <div className="alert alert-danger">{err}</div>}
      {loading ? (
        <p className="text-muted">Caricamentoâ€¦</p>
      ) : overview ? (
        <>
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <KpiCard title="Totale ordini" value={overview.tot_ordini ?? 0} />
            </div>
            <div className="col-md-3">
              <KpiCard title="Fatturato" value={euro(overview.tot_fatturato)} />
            </div>
            <div className="col-md-3">
              <KpiCard title="Provvigioni maturate" value={euro(overview.tot_commissioni)} />
            </div>
            <div className="col-md-3">
              <KpiCard title="Commissioni in attesa" value={euro(overview.commissioni_pendenti)} />
            </div>
          </div>

          {overview.agenti.length === 0 ? (
            <div className="alert alert-info">Nessun agente rilevato.</div>
          ) : (
            <div className="row g-3">
              {overview.agenti.map((a) => (
                <div key={a.id} className="col-12 col-md-6 col-xl-4">
                  <div className="card shadow-sm h-100">
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h5 className="m-0">
                            {a.nome || a.cognome ? `${a.nome} ${a.cognome}`.trim() : a.username}
                          </h5>
                          <div className="text-muted small">
                            @{a.username} {a.store ? `â€¢ ${a.store}` : ""}
                          </div>
                        </div>
                        <span className="badge bg-secondary">AGENTE</span>
                      </div>

                      <div className="mt-3">
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Fatturato</span>
                          <strong>{euro(a.tot_fatturato)}</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Provvigioni maturate</span>
                          <strong>{euro(a.provvigioni_maturate)}</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Commissioni in attesa</span>
                          <strong>{euro(a.provvigioni_pendenti)}</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted"># Ordini (pagati / non pagati)</span>
                          <strong>
                            {a.pagati} / {a.non_pagati}
                          </strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Ultimo ordine</span>
                          <strong>{formatLastOrder(a.ultimo_ordine)}</strong>
                        </div>
                      </div>

                      <div className="mt-3 d-flex gap-2">
                        <button
                          className="btn btn-outline-dark btn-sm"
                          onClick={() => nav(`/ordini?q=${encodeURIComponent(a.username)}`)}
                          title="Vedi ordini di questo agente"
                        >
                          Ordini
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => nav(`/dashboard-agente?user_id=${a.id}`)}
                          title="Apri dashboard dettagliata"
                        >
                          Dettagli
                        </button>
                      </div>

                      <div className="mt-2 small text-muted">
                        Totale pendente: <strong>{euro(a.provvigioni_pendenti)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : detail ? (
        <>
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-xl-3">
              <KpiCard title="Totale ordini" value={detail.tot_ordini ?? 0} />
            </div>
            <div className="col-12 col-md-6 col-xl-3">
              <KpiCard title="Fatturato totale" value={euro(detail.tot_fatturato)} />
            </div>
            <div className="col-12 col-md-6 col-xl-3">
              <KpiCard title="Provvigioni maturate" value={euro(detail.tot_commissioni)} />
            </div>
            <div className="col-12 col-md-6 col-xl-3">
              <KpiCard title="Provvigioni in attesa" value={euro(detail.commissioni_pendenti)} />
            </div>
          </div>

          <div className="card shadow-sm mb-4">
            <div className="card-header fw-semibold">
              Andamento mensile
            </div>

            <div className="card-body">
              <div className="row g-4">
                <div className="col-12 col-xl-6">
                  <h5 className="mb-3">Questo mese</h5>

                  <div className="row g-3">
                    <div className="col-12 col-sm-4">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Ordini</div>
                        <div className="fs-4 fw-bold">
                          {detail.mese_corrente?.ordini ?? 0}
                        </div>
                        <div className="small mt-1">
                          {detail.variazioni?.ordini == null
                            ? "Nuovo"
                            : `${detail.variazioni.ordini > 0 ? "+" : ""}${Number(detail.variazioni.ordini).toFixed(2)}%`}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-4">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Fatturato</div>
                        <div className="fs-4 fw-bold">
                          {euro(detail.mese_corrente?.fatturato)}
                        </div>
                        <div className="small mt-1">
                          {detail.variazioni?.fatturato == null
                            ? "Nuovo"
                            : `${detail.variazioni.fatturato > 0 ? "+" : ""}${Number(detail.variazioni.fatturato).toFixed(2)}%`}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-4">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Provvigioni</div>
                        <div className="fs-4 fw-bold">
                          {euro(detail.mese_corrente?.provvigioni)}
                        </div>
                        <div className="small mt-1">
                          {detail.variazioni?.provvigioni == null
                            ? "Nuovo"
                            : `${detail.variazioni.provvigioni > 0 ? "+" : ""}${Number(detail.variazioni.provvigioni).toFixed(2)}%`}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-xl-6">
                  <h5 className="mb-3">Mese scorso</h5>

                  <div className="row g-3">
                    <div className="col-12 col-sm-4">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Ordini</div>
                        <div className="fs-4 fw-bold">
                          {detail.mese_precedente?.ordini ?? 0}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-4">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Fatturato</div>
                        <div className="fs-4 fw-bold">
                          {euro(detail.mese_precedente?.fatturato)}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-4">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Provvigioni</div>
                        <div className="fs-4 fw-bold">
                          {euro(detail.mese_precedente?.provvigioni)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="small text-muted mt-3">
                Le percentuali confrontano questo mese con il mese precedente.
              </div>
            </div>
          </div>

          <div className="card shadow-sm mb-4">
            <div className="card-header fw-semibold">
              Portafoglio Aziende
            </div>

            <div className="card-body">
              <div className="row g-3 mb-4">
                <div className="col-6 col-xl-3">
                  <KpiCard
                    title="Aziende attuali"
                    value={detail.portafoglio?.totale_attuali ?? 0}
                  />
                </div>

                <div className="col-6 col-xl-3">
                  <KpiCard
                    title="Acquisite"
                    value={detail.portafoglio?.acquisite_attuali ?? 0}
                  />
                </div>

                <div className="col-6 col-xl-3">
                  <KpiCard
                    title="Assegnate"
                    value={detail.portafoglio?.assegnate_attuali ?? 0}
                  />
                </div>

                <div className="col-6 col-xl-3">
                  <KpiCard
                    title="Acquisite storiche"
                    value={detail.portafoglio?.acquisite_storiche ?? 0}
                  />
                </div>
              </div>

              {(detail.portafoglio?.aziende || []).length === 0 ? (
                <div className="text-muted">
                  Nessuna azienda attualmente in portafoglio.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Codice</th>
                        <th>Azienda</th>
                        <th>Rapporto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.portafoglio?.aziende || []).map((azienda) => (
                        <tr key={azienda.id}>
                          <td>
                            <strong>{azienda.code || "-"}</strong>
                          </td>
                          <td>{azienda.name || "-"}</td>
                          <td>
                            <span
                              className={
                                azienda.tipo_portafoglio === "ACQUISITA"
                                  ? "badge bg-success"
                                  : "badge bg-primary"
                              }
                            >
                              {azienda.tipo_portafoglio === "ACQUISITA"
                                ? "Acquisita"
                                : "Assegnata"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="small text-muted mt-3">
                Le aziende acquisite storiche restano attribuite all'agente
                che le ha realmente acquisite anche dopo una riassegnazione.
              </div>
            </div>
          </div>

          <div className="card shadow-sm mb-4">
            <div className="card-header fw-semibold">
              Storico completo ordini e liquidazioni
            </div>
            <div className="card-body">
              <div className="row g-3 mb-4">
                {[
                  ["Ordini non incassati", detail.riepilogo_pagamenti?.ordini_da_incassare ?? 0, false],
                  ["Provvigioni previste", detail.riepilogo_pagamenti?.provvigioni_previste, true],
                  ["Provvigioni maturate", detail.riepilogo_pagamenti?.provvigioni_maturate, true],
                  ["Provvigioni liquidate", detail.riepilogo_pagamenti?.provvigioni_liquidate, true],
                  ["Residuo complessivo", detail.riepilogo_pagamenti?.provvigioni_residue, true],
                  ["Residuo sospeso", detail.riepilogo_pagamenti?.provvigioni_sospese, true],
                  ["Attualmente liquidabile", detail.riepilogo_pagamenti?.provvigioni_liquidabili, true],
                ].map(([label, value, money]) => (
                  <div className="col-6 col-lg-3" key={label}>
                    <div className="border rounded p-3 h-100">
                      <div className="small text-muted">{label}</div>
                      <div className="fw-bold fs-5">
                        {money ? euro(value) : value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mb-3">
                <input
                  type="search"
                  className="form-control"
                  placeholder="Cerca ordine o azienda..."
                  aria-label="Cerca ordine o azienda"
                  onChange={(event) => {
                    setStoricoSearch(event.target.value);
                  }}
                />
              </div>

              <div className="table-responsive">
                <table className="table table-sm table-striped align-middle">
                  <thead>
                    <tr>
                      <th>Ordine</th>
                      <th>Azienda</th>
                      <th>Data</th>
                      <th>Pagamento azienda</th>
                      <th className="text-end">Totale</th>
                      <th className="text-end">Provvigione</th>
                      <th className="text-end">Liquidata</th>
                      <th className="text-end">Liquidabile</th>
                      <th className="text-end">Residua</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.storico_ordini || [])
                      .filter((o) => {
                        const query = storicoSearch.trim().toLowerCase();
                        return !query || `${o.id} ${o.azienda || ""}`
                          .toLowerCase()
                          .includes(query);
                      })
                      .map((o) => (
                      <React.Fragment key={o.id}>
                        <tr>
                          <td>#{o.id}</td>
                          <td>{o.azienda || "Non indicata"}</td>
                          <td>{fmtDateTime(o.date)}</td>
                          <td>
                            <span className={
                              o.azienda_pagato
                                ? "badge bg-success"
                                : "badge bg-warning text-dark"
                            }>
                              {o.azienda_pagato ? "Saldato" : "Da incassare"}
                            </span>
                            <div className="small text-muted">
                              {o.metodo_pagamento || "Metodo non indicato"}
                            </div>
                          </td>
                          <td className="text-end">{euro(o.totale)}</td>
                          <td className="text-end">{euro(o.provvigione_importo)}</td>
                          <td className="text-end">{euro(o.provvigione_pagata)}</td>
                          <td className="text-end">{euro(o.provvigione_liquidabile)}</td>
                          <td className="text-end">{euro(o.provvigione_residua)}</td>
                        </tr>
                        {(o.pagamenti_agente || []).map((payment) => (
                          <tr
                            key={`payment-${o.id}-${payment.id}`}
                          >
                            <td colSpan={9} className="ps-4 small text-muted">
                              Liquidazione: {fmtDateTime(payment.data)}
                              {" · "}{euro(payment.importo)}
                              {" · "}{payment.metodo || "Metodo non indicato"}
                              {payment.riferimento
                                ? ` · Rif. ${payment.riferimento}`
                                : ""}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {(detail.storico_ordini || []).length === 0 && (
                <div className="text-muted">
                  Nessun ordine attribuito a questo agente.
                </div>
              )}
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-header fw-semibold">
              Ultimi ordini e situazione pagamenti
            </div>
            <div className="card-body p-0">
              {(detail.ultimi_ordini || []).length === 0 ? (
                <div className="p-3 text-muted">Nessun ordine recente.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm m-0 align-middle">
                    <thead>
                      <tr>
                        <th>Ordine</th>
                        <th>Azienda</th>
                        <th>Data</th>
                        <th>Pagamento azienda</th>
                        <th className="text-end">Totale</th>
                        <th>Provvigione</th>
                        <th className="text-end">Pagata agente</th>
                        <th className="text-end">Residua</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.ultimi_ordini.map((o) => (
                        <tr key={o.id}>
                          <td>#{o.id}</td>
                          <td>{o.azienda || "Non indicata"}</td>
                          <td>{fmtDateTime(o.date)}</td>
                          <td>
                            <span className={
                              o.azienda_pagato
                                ? "badge bg-success"
                                : "badge bg-warning text-dark"
                            }>
                              {o.azienda_pagato ? "Saldato" : "Da incassare"}
                            </span>
                            <div className="small text-muted mt-1">
                              {o.metodo_pagamento === "ONLINE"
                                ? "Online"
                                : o.metodo_pagamento === "CONSEGNA"
                                ? "Alla consegna"
                                : o.metodo_pagamento || "Metodo non indicato"}
                            </div>
                          </td>
                          <td className="text-end">{euro(o.totale)}</td>
                          <td>
                            <div>{euro(o.commissione)}</div>
                            <span className={badgeClassFor(o.stato)}>
                              {statusIt(o.stato)}
                            </span>
                          </td>
                          <td className="text-end">
                            {euro(o.provvigione_pagata)}
                          </td>
                          <td className="text-end">
                            {euro(o.provvigione_residua)}
                            {!o.azienda_pagato || !o.ordine_completato ||
                            !["EARNED", "PAID"].includes(o.stato) ? (
                              <div className="small text-warning">
                                Non liquidabile
                              </div>
                            ) : (
                              <div className="small text-success">
                                {Number(o.provvigione_residua || 0) > 0
                                  ? "Liquidabile"
                                  : "Saldo liquidato"}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="card-footer">
              <div>
                Provvigioni in attesa:{" "}
                <strong>{euro(detail.commissioni_pendenti)}</strong>
              </div>
              <div className="small text-muted mt-1">
                La tabella mostra gli ultimi cinque ordini.
                Il residuo è liquidabile soltanto dopo il saldo
                completo dell'azienda e il completamento dell'ordine.
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="alert alert-info">Nessun dato da mostrare.</div>
      )}
    </div>
  );
}
